import React from "react";
import "./App.css";
import "./removeScrollIndicator.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider, Helmet } from 'react-helmet-async';
import { CheckCircle, Clock, Shield, Smartphone, Leaf, CreditCard, DollarSign } from "lucide-react";
import AnimatedProcessSection from "./components/AnimatedProcessSection";
import LiveChatWidget from "./components/LiveChatWidget";
import HolidayNotice from "./components/HolidayNotice";
import Snowflakes from "./components/Snowflakes";
import ChristmasSanta from "./components/ChristmasSanta";
// Christmas lights removed - cleaner design
import RateCalculator from "./pages/RateCalculator";
import FormSubmission from "./pages/FormSubmission";
import TrackSubmission from "./pages/TrackSubmission";
import AdminDashboard from "./pages/AdminDashboard";
import AdWordsDashboard from "./pages/AdWordsDashboard";
import AcceptedGiftCards from "./pages/AcceptedGiftCards";
import FAQs from "./pages/FAQs";
import GettingStarted from "./pages/GettingStarted";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import RefundPolicy from "./pages/RefundPolicy";
import CookiePolicy from "./pages/CookiePolicy";
import TermsOfService from "./pages/TermsOfService";
import SecurityPage from "./pages/SecurityPage";
import GiftCardGuide from "./pages/GiftCardGuide";
import GiftCardValues from "./pages/GiftCardValues";
import SafetyGuide from "./pages/SafetyGuide";
import SeasonalTrends from "./pages/SeasonalTrends";
import CompareRates from "./pages/CompareRates";
import BrandPage from "./pages/BrandPage";
import BrandLandingPage from "./pages/BrandLandingPage";
import LocationPage from "./pages/LocationPage";
import EvidenceViewer from "./pages/EvidenceViewer";
import BlogIndex from "./pages/BlogIndex";
import BlogPost from "./pages/BlogPost";
import NotFound from "./pages/NotFound";
import SellGiftCardsOnline from "./pages/SellGiftCardsOnline";

// ============================================
// SEASONAL CONFIGURATION
// ============================================
// Christmas theme configuration - Auto-activates Dec 1 to Jan 6
const CHRISTMAS_START_DATE = new Date('2025-12-01');
const CHRISTMAS_END_DATE = new Date('2026-01-06');

// PREVIEW MODE - Set to false for auto-activation on December 1st
const PREVIEW_CHRISTMAS_THEME = false; // Auto-activate on Dec 1st

// Check if we're in Christmas season
const isChristmasSeason = () => {
  if (PREVIEW_CHRISTMAS_THEME) return true; // Manual preview mode
  const now = new Date();
  return now >= CHRISTMAS_START_DATE && now <= CHRISTMAS_END_DATE;
};

// Holiday notice shows during Christmas season
const SHOW_HOLIDAY_NOTICE = isChristmasSeason();

// Visible scroll-based movement effect
// Animated Counter Component - Triggers only on scroll into view
const AnimatedCounter = ({ target, duration = 2000 }) => {
  const [count, setCount] = React.useState(0);
  const [isVisible, setIsVisible] = React.useState(false);
  const counterRef = React.useRef(null);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { 
        threshold: 0.6, // Trigger when 60% of element is visible
        rootMargin: '0px 0px -100px 0px' // Start animation slightly before fully visible
      }
    );

    if (counterRef.current) {
      observer.observe(counterRef.current);
    }

    return () => observer.disconnect();
  }, [isVisible]);

  React.useEffect(() => {
    if (!isVisible) return;

    let startTime;
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      
      // Easing function for smooth, impressive animation
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const currentCount = Math.floor(easeOutQuart * target);
      
      setCount(currentCount);

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [isVisible, target, duration]);

  const formatNumber = (num) => {
    return num.toLocaleString();
  };

  return (
    <div ref={counterRef} className="text-pink-600 font-bold text-lg md:text-xl">
      {formatNumber(count)}
    </div>
  );
};

// Custom hook for scroll movement effect
const useScrollMovement = () => {
  React.useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.pageYOffset;
      const parallaxElements = document.querySelectorAll('.scroll-move');
      
      parallaxElements.forEach((element, index) => {
        const rect = element.getBoundingClientRect();
        const elementTop = rect.top + window.pageYOffset;
        const elementHeight = rect.height;
        const windowHeight = window.innerHeight;
        
        // Calculate if element is in viewport
        const elementCenter = elementTop + (elementHeight / 2);
        const distanceFromCenter = Math.abs((window.pageYOffset + windowHeight / 2) - elementCenter);
        const maxDistance = windowHeight / 2 + elementHeight / 2;
        
        if (distanceFromCenter < maxDistance) {
          // Element is visible, apply movement
          const progress = 1 - (distanceFromCenter / maxDistance);
          const moveY = progress * 15; // Move up to 15px
          const scaleValue = 1 + (progress * 0.05); // Scale up to 1.05
          
          element.style.transform = `translateY(-${moveY}px) scale(${scaleValue})`;
          element.style.opacity = 0.7 + (progress * 0.3); // Fade effect
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Run once on mount
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Header Component
const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const [isHeaderVisible, setIsHeaderVisible] = React.useState(true);
  const [lastScrollY, setLastScrollY] = React.useState(0);

  React.useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      // Show header when scrolling up, hide when scrolling down
      if (currentScrollY < lastScrollY || currentScrollY < 100) {
        setIsHeaderVisible(true);
      } else if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsHeaderVisible(false);
        setIsMobileMenuOpen(false); // Close mobile menu when hiding header
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  return (
    <header className={`bg-white sticky top-0 z-50 transition-transform duration-300 border-b border-gray-200 ${
      isHeaderVisible ? 'translate-y-0' : '-translate-y-full'
    }`}>
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex items-center">
            <a href="/" className="flex items-center">
              <div className="bg-gradient-to-r from-pink-500 to-pink-600 p-2 rounded-full">
                <CreditCard className="h-6 w-6 text-white" />
              </div>
              <div className="ml-3 premium-brand">
                <div className="brand-name">Cashifygcmart</div>
                <div className="brand-tagline">Instant Offers, Same-Day Payments</div>
              </div>
            </a>
          </div>
          
          {/* Desktop Navigation */}
          <div className="hidden md:block">
            <div className="ml-10 flex items-baseline space-x-8">
              <a href="/" className="text-gray-900 hover:text-pink-600 px-3 py-2 text-sm font-medium transition-all duration-200 hover:scale-110 relative hover:font-semibold">Home</a>
              <a href="/rate-calculator" className="text-gray-700 hover:text-pink-600 px-3 py-2 text-sm font-medium transition-all duration-200 hover:scale-110 relative hover:font-semibold">Rate Calculator</a>
              <a href="/form-submission" className="text-gray-700 hover:text-pink-600 px-3 py-2 text-sm font-medium transition-all duration-200 hover:scale-110 relative hover:font-semibold">Form Submission</a>
              <a href="/track-submission" className="text-gray-700 hover:text-pink-600 px-3 py-2 text-sm font-medium transition-all duration-200 hover:scale-110 relative hover:font-semibold">Track Order</a>
              
              {/* Talk to Manager */}
              <button 
                onClick={(e) => {
                  e.preventDefault();
                  console.log('Talk to Manager clicked - Opening Zoho');
                  
                  // Wait for Zoho to be ready and open chat
                  if (window.$zoho && window.$zoho.salesiq && window.$zoho.salesiq.floatwindow) {
                    try {
                      window.$zoho.salesiq.floatwindow.visible('show');
                      console.log('Zoho chat opened');
                    } catch (error) {
                      console.error('Error opening Zoho:', error);
                      // Fallback: try to click the Zoho button if it exists
                      const zohoBtn = document.querySelector('.zsiq_flt_rel, .siqicon, [id*="zsiq"]');
                      if (zohoBtn) {
                        zohoBtn.click();
                      }
                    }
                  } else {
                    console.warn('Zoho not loaded yet');
                    // Try again after a short delay
                    setTimeout(() => {
                      if (window.$zoho?.salesiq?.floatwindow) {
                        window.$zoho.salesiq.floatwindow.visible('show');
                      }
                    }, 500);
                  }
                }}
                className="text-gray-700 hover:text-pink-600 px-3 py-2 text-sm font-medium transition-all duration-200 hover:scale-110 relative hover:font-semibold flex items-center gap-1"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                Talk to Manager
              </button>
              
              {/* Resources Dropdown */}
              <div className="relative group">
                <button className="text-gray-700 hover:text-pink-600 px-3 py-2 text-sm font-medium transition-all duration-200 hover:scale-110 relative hover:font-semibold">
                  Resources ▼
                </button>
                <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                  <div className="py-2">
                    <a href="/blog" className="block px-4 py-2 text-sm text-gray-700 hover:bg-pink-50 hover:text-pink-600">📝 Blog</a>
                    <a href="/guide/how-to-sell-gift-cards" className="block px-4 py-2 text-sm text-gray-700 hover:bg-pink-50 hover:text-pink-600">Complete Selling Guide</a>
                    <a href="/gift-card-values" className="block px-4 py-2 text-sm text-gray-700 hover:bg-pink-50 hover:text-pink-600">Gift Card Values 2024</a>
                    <a href="/safety-guide" className="block px-4 py-2 text-sm text-gray-700 hover:bg-pink-50 hover:text-pink-600">Safety & Security Guide</a>
                    <a href="/seasonal-trends" className="block px-4 py-2 text-sm text-gray-700 hover:bg-pink-50 hover:text-pink-600">Market Trends & Analysis</a>
                    <a href="/compare-rates" className="block px-4 py-2 text-sm text-gray-700 hover:bg-pink-50 hover:text-pink-600">Rate Comparison Tool</a>
                  </div>
                </div>
              </div>
              
              <a href="/accepted-cards" className="text-gray-700 hover:text-pink-600 px-3 py-2 text-sm font-medium transition-all duration-200 hover:scale-110 relative hover:font-semibold">Accepted Cards</a>
              <a href="/faqs" className="text-gray-700 hover:text-pink-600 px-3 py-2 text-sm font-medium transition-all duration-200 hover:scale-110 relative hover:font-semibold">FAQs</a>
            </div>
          </div>
          
          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-pink-500"
              aria-expanded="false"
            >
              <span className="sr-only">Open main menu</span>
              {!isMobileMenuOpen ? (
                <svg className="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              ) : (
                <svg className="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
            </button>
          </div>
        </div>
        
        {/* Mobile menu, show/hide based on menu state */}
        {isMobileMenuOpen && (
          <div className="md:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-white border-t border-gray-200">
              <a href="/" className="text-gray-900 hover:bg-pink-50 hover:text-pink-600 block px-4 py-4 text-base font-medium rounded-lg transition-colors flex items-center space-x-3">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
                <span>Home</span>
              </a>
              <a href="/rate-calculator" className="text-gray-700 hover:bg-pink-50 hover:text-pink-600 block px-4 py-4 text-base font-medium rounded-lg transition-colors flex items-center space-x-3">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
                <span>Rate Calculator</span>
              </a>
              <a href="/form-submission" className="text-gray-700 hover:bg-pink-50 hover:text-pink-600 block px-4 py-4 text-base font-medium rounded-lg transition-colors flex items-center space-x-3 bg-gradient-to-r from-pink-50 to-purple-50 border-2 border-pink-200">
                <svg className="w-5 h-5 text-pink-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-pink-600 font-semibold">Sell Card Now</span>
              </a>
              <a href="/track-submission" className="text-gray-700 hover:bg-pink-50 hover:text-pink-600 block px-4 py-4 text-base font-medium rounded-lg transition-colors flex items-center space-x-3">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <span>Track Order</span>
              </a>
              
              {/* Talk to Manager Button - Mobile */}
              <button 
                onClick={(e) => {
                  e.preventDefault();
                  setIsMobileMenuOpen(false);
                  console.log('Talk to Manager clicked (Mobile) - Opening Zoho');
                  
                  setTimeout(() => {
                    if (window.$zoho && window.$zoho.salesiq && window.$zoho.salesiq.floatwindow) {
                      try {
                        window.$zoho.salesiq.floatwindow.visible('show');
                        console.log('Zoho chat opened');
                      } catch (error) {
                        console.error('Error opening Zoho:', error);
                        const zohoBtn = document.querySelector('.zsiq_flt_rel, .siqicon, [id*="zsiq"]');
                        if (zohoBtn) {
                          zohoBtn.click();
                        }
                      }
                    } else {
                      console.warn('Zoho not loaded yet');
                      setTimeout(() => {
                        if (window.$zoho?.salesiq?.floatwindow) {
                          window.$zoho.salesiq.floatwindow.visible('show');
                        }
                      }, 500);
                    }
                  }, 300);
                }}
                className="text-white bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-700 hover:to-purple-700 block w-full px-4 py-4 text-base font-medium rounded-lg transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105 flex items-center justify-center space-x-3"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <span>Talk to Manager</span>
              </button>
              
              {/* Resources Section */}
              <div className="border-t border-gray-100 pt-3 mt-3">
                <div className="text-gray-500 px-4 py-2 text-sm font-semibold flex items-center space-x-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                  <span>Resources</span>
                </div>
                <a href="/blog" className="text-gray-700 hover:bg-pink-50 hover:text-pink-600 block px-6 py-3 text-sm rounded-lg transition-colors">📝 Blog</a>
                <a href="/guide/how-to-sell-gift-cards" className="text-gray-700 hover:bg-pink-50 hover:text-pink-600 block px-6 py-3 text-sm rounded-lg transition-colors">📖 Complete Selling Guide</a>
                <a href="/gift-card-values" className="text-gray-700 hover:bg-pink-50 hover:text-pink-600 block px-6 py-3 text-sm rounded-lg transition-colors">💎 Gift Card Values 2024</a>
                <a href="/safety-guide" className="text-gray-700 hover:bg-pink-50 hover:text-pink-600 block px-6 py-3 text-sm rounded-lg transition-colors">🛡️ Safety & Security Guide</a>
                <a href="/seasonal-trends" className="text-gray-700 hover:bg-pink-50 hover:text-pink-600 block px-6 py-3 text-sm rounded-lg transition-colors">📊 Market Trends</a>
                <a href="/compare-rates" className="text-gray-700 hover:bg-pink-50 hover:text-pink-600 block px-6 py-3 text-sm rounded-lg transition-colors">⚖️ Rate Comparison</a>
              </div>
              
              <a href="/accepted-cards" className="text-gray-700 hover:bg-pink-50 hover:text-pink-600 block px-4 py-4 text-base font-medium border-t border-gray-100 pt-3 mt-3 rounded-lg transition-colors flex items-center space-x-3">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
                <span>Accepted Cards</span>
              </a>
              <a href="/faqs" className="text-gray-700 hover:bg-pink-50 hover:text-pink-600 block px-4 py-4 text-base font-medium rounded-lg transition-colors flex items-center space-x-3">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>FAQs</span>
              </a>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

// Unified Support Button for Mobile - Premium Clean Design (Available on all pages)
const UnifiedSupportButton = ({ setLiveChatOpen }) => {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [isVisible, setIsVisible] = React.useState(true); // Always visible

  // Optional: Can add minimal scroll detection if needed
  React.useEffect(() => {
    const handleScroll = () => {
      // Show button always, but could add minimal scroll if needed
      setIsVisible(true);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    // Initial visibility
    setIsVisible(true);
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (!isVisible) return null;

  const handleAIChat = () => {
    // Open Live Chat (which has AI + FAQ capabilities)
    setIsMenuOpen(false);
    setLiveChatOpen(true);
  };

  const handleLiveChat = () => {
    // Open AI Live Chat with Zoho transfer capability
    setIsMenuOpen(false);
    setLiveChatOpen(true);
  };

  return (
    <>
      {/* Overlay */}
      {isMenuOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-30 z-40 transition-opacity duration-300"
          onClick={() => setIsMenuOpen(false)}
        />
      )}

      {/* Menu Options - Works on both mobile and desktop */}
      {isMenuOpen && (
        <div className="fixed bottom-24 right-6 z-50 animate-slide-up">
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden min-w-[240px]">
            {/* AI Assistant (Basic FAQs) */}
            <button
              onClick={handleAIChat}
              className="w-full px-5 py-4 flex items-center space-x-3 hover:bg-gradient-to-r hover:from-pink-50 hover:to-purple-50 transition-all duration-200 border-b border-gray-100"
            >
              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                <span className="text-xl">🤖</span>
              </div>
              <div className="text-left">
                <div className="font-semibold text-gray-900 text-sm">AI Assistant</div>
                <div className="text-xs text-gray-500">Quick FAQs</div>
              </div>
            </button>

            {/* Live Chat (AI with Human Transfer) */}
            <button
              onClick={handleLiveChat}
              className="w-full px-5 py-4 flex items-center space-x-3 hover:bg-gradient-to-r hover:from-pink-50 hover:to-purple-50 transition-all duration-200 border-b border-gray-100"
            >
              <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center">
                <span className="text-xl">💬</span>
              </div>
              <div className="text-left">
                <div className="font-semibold text-gray-900 text-sm">Live Chat</div>
                <div className="text-xs text-gray-500">Smart support</div>
              </div>
            </button>

            {/* Submit Card */}
            <a
              href="/form-submission"
              className="w-full px-5 py-4 flex items-center space-x-3 hover:bg-gradient-to-r hover:from-pink-50 hover:to-purple-50 transition-all duration-200 border-b border-gray-100"
              onClick={() => setIsMenuOpen(false)}
            >
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center">
                <span className="text-xl">💳</span>
              </div>
              <div className="text-left">
                <div className="font-semibold text-gray-900 text-sm">Submit Card</div>
                <div className="text-xs text-gray-500">Sell your card</div>
              </div>
            </a>

            {/* Track Order */}
            <a
              href="/track-submission"
              className="w-full px-5 py-4 flex items-center space-x-3 hover:bg-gradient-to-r hover:from-pink-50 hover:to-purple-50 transition-all duration-200 border-b border-gray-100"
              onClick={() => setIsMenuOpen(false)}
            >
              <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
                <span className="text-xl">🔍</span>
              </div>
              <div className="text-left">
                <div className="font-semibold text-gray-900 text-sm">Track Order</div>
                <div className="text-xs text-gray-500">Check status</div>
              </div>
            </a>

            {/* Check Rates */}
            <a
              href="/rate-calculator"
              className="w-full px-5 py-4 flex items-center space-x-3 hover:bg-gradient-to-r hover:from-pink-50 hover:to-purple-50 transition-all duration-200"
              onClick={() => setIsMenuOpen(false)}
            >
              <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center">
                <span className="text-xl">📊</span>
              </div>
              <div className="text-left">
                <div className="font-semibold text-gray-900 text-sm">Check Rates</div>
                <div className="text-xs text-gray-500">View offers</div>
              </div>
            </a>
          </div>
        </div>
      )}

      {/* Main Support Button - Fixed Bottom Right (Mobile AND Desktop) */}
      <button
        onClick={() => setIsMenuOpen(!isMenuOpen)}
        aria-label="Support Menu"
        className="fixed bottom-6 right-6 z-50 w-16 h-16 bg-gradient-to-br from-pink-600 to-purple-600 text-white rounded-full shadow-2xl hover:shadow-pink-500/50 transition-all duration-300 hover:scale-110 flex items-center justify-center group"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
        </svg>
        {/* Subtle pulse indicator */}
        <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full animate-pulse"></span>
      </button>
    </>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section className="bg-gradient-to-br from-pink-50 to-blue-50 py-8 md:py-16">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          <div>
            <h1 className="text-2xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight md:leading-tight">
              <span className="block">Sell Unused Gift Cards for</span>
              <span className="text-pink-600 block">Same-Day Cash Online</span>
            </h1>
            <p className="mt-4 md:mt-6 text-base md:text-lg text-gray-600 max-w-lg leading-relaxed">
              CashifyGCmart is the trusted gift card exchange platform offering instant quotes, competitive rates up to 93%, and secure same-day payments. Turn your unwanted gift cards into cash today.
            </p>
            <div className="mt-6 md:mt-8 flex flex-col sm:flex-row gap-3 md:gap-4">
              <a href="/form-submission">
                <button className="w-full sm:w-auto bg-pink-600 hover:bg-pink-700 text-white px-6 md:px-8 py-3 md:py-4 rounded-lg font-semibold text-base md:text-lg transition-all duration-300 hover:scale-105 hover:shadow-xl transform">
                  Start Here
                </button>
              </a>
              <a href="/accepted-cards">
                <button className="w-full sm:w-auto bg-gray-900 hover:bg-gray-800 text-white px-6 md:px-8 py-3 md:py-4 rounded-lg font-semibold text-base md:text-lg transition-all duration-300 hover:scale-105 hover:shadow-xl transform">
                  See 100+ Accepted Brands
                </button>
              </a>
            </div>
            
            {/* Trust Badges */}
            <div className="mt-8 md:mt-10">
              <p className="text-sm text-gray-500 mb-4">Trusted by thousands of customers nationwide</p>
              <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-green-600" />
                  <span>SSL Secured</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span>No Hidden Fees</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-green-600" />
                  <span>Same-Day Payouts</span>
                </div>
                <div className="flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-green-600" />
                  <span>230+ Vendors</span>
                </div>
              </div>
            </div>
          </div>
          <div className="relative mt-8 lg:mt-0">
            <img 
              src="/images/hero-illustration.png" 
              alt="Happy customers exchanging gift cards for cash online - Cashifygcmart secure platform" 
              className="hero-image w-full h-auto object-contain max-w-full"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

// Statistics Section
const StatsSection = () => {
  const [selectedStat, setSelectedStat] = React.useState(null);

  const stats = [
    { number: "100+", label: "Choose Your Card", description: "Select a brand and enter the balance to view current offers." },
    { number: "92%", label: "Get an Instant Quote", description: "Instant transparent pricing so you know what to expect." },
    { number: "230+", label: "Trusted Vendor Network", description: "Expert resources from vetted vendors." }
  ];

  const handleStatClick = (index) => {
    setSelectedStat(selectedStat === index ? null : index);
  };

  return (
    <section className="py-12 md:py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div className="mb-10 md:mb-12">
          <h2 className="text-pink-600 font-semibold text-base md:text-lg mb-3">First Time Selling a Gift Card?</h2>
          <h3 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900">Easy setup, clear rates, and same-day payout options.</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 lg:gap-8">
          {stats.map((stat, index) => {
            const isSelected = selectedStat === index;
            return (
              <div key={index} className="text-center">
                <div 
                  onClick={() => handleStatClick(index)}
                  className={`scroll-move rounded-xl md:rounded-2xl p-4 md:p-6 lg:p-8 transition-all duration-300 cursor-pointer transform
                    ${isSelected 
                      ? 'bg-pink-200 scale-105 shadow-xl' 
                      : 'bg-pink-100 hover:bg-pink-200 hover:scale-105 hover:shadow-xl'
                    }
                    active:scale-95 touch-manipulation`}
                  style={{ 
                    WebkitTapHighlightColor: 'transparent'
                  }}
                >
                  <div className={`text-2xl md:text-3xl lg:text-4xl font-bold mb-2 transition-colors duration-200 ${
                    isSelected ? 'text-pink-700' : 'text-pink-600 hover:text-pink-700'
                  }`}>
                    {stat.number}
                  </div>
                  <div className="text-sm md:text-base lg:text-lg font-semibold text-gray-900 mb-2">{stat.label}</div>
                  <div className="text-xs md:text-sm text-gray-600">{stat.description}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Why Choose Us Section
const WhyChooseUsSection = () => {
  const [selectedFeature, setSelectedFeature] = React.useState(null);

  const features = [
    {
      icon: <Shield className="h-12 w-12" />,
      title: "Honest Pricing, Always",
      description: "Clear, up-front offers — no hidden clauses, ever."
    },
    {
      icon: <Clock className="h-12 w-12" />,
      title: "Quick Turnaround",
      description: "Fast verification and prompt payouts so you don't wait for your cash."
    },
    {
      icon: <CheckCircle className="h-12 w-12" />,
      title: "Consistent Results",
      description: "Dependable processing and predictable outcomes every time you sell."
    },
    {
      icon: <Smartphone className="h-12 w-12" />,
      title: "Designed for Everyone",
      description: "Intuitive flows and friction-free steps — sell a card in minutes."
    },
    {
      icon: <Shield className="h-12 w-12" />,
      title: "Privacy First",
      description: "Encrypted storage and strict fraud checks protect your information."
    },
    {
      icon: <CheckCircle className="h-12 w-12" />,
      title: "Help When You Need It",
      description: "Responsive support via chat or email — real people, real help."
    }
  ];

  const handleFeatureClick = (index) => {
    setSelectedFeature(selectedFeature === index ? null : index);
  };

  return (
    <section className="py-12 md:py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div className="mb-8 md:mb-10">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 md:mb-4">Why Choose Us?</h2>
          <p className="text-sm md:text-base lg:text-lg text-gray-600 max-w-3xl mx-auto px-4">
            Sell your unused gift cards with ease, speed, and confidence. We offer a secure, hassle-free process and 
            transparent rates to ensure you get the best value, every time.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 lg:gap-8">
          {features.map((feature, index) => {
            const isSelected = selectedFeature === index;
            return (
              <div 
                key={index} 
                onClick={() => handleFeatureClick(index)}
                className={`scroll-move p-4 md:p-6 lg:p-8 rounded-xl md:rounded-2xl transition-all duration-300 cursor-pointer transform
                  ${isSelected 
                    ? 'bg-pink-600 text-white scale-105 shadow-2xl' 
                    : 'bg-gray-50 hover:bg-white hover:scale-105 hover:shadow-2xl'
                  }
                  active:scale-95 touch-manipulation`}
                style={{ 
                  WebkitTapHighlightColor: 'transparent'
                }}
              >
                <div className={`flex justify-center mb-4 md:mb-6 transform transition-all duration-200 
                  ${isSelected ? 'scale-110' : 'hover:scale-110'}`}>
                  {React.cloneElement(feature.icon, {
                    className: `h-8 w-8 md:h-10 md:w-10 lg:h-12 lg:w-12 transition-colors duration-200 ${
                      isSelected ? 'text-white' : 'text-pink-600'
                    }`
                  })}
                </div>
                <h4 className={`text-base md:text-lg lg:text-xl font-semibold mb-2 md:mb-4 transition-colors duration-200 ${
                  isSelected ? 'text-white' : 'text-gray-900'
                }`}>
                  {feature.title}
                </h4>
                <p className={`text-xs md:text-sm lg:text-base transition-colors duration-200 ${
                  isSelected ? 'text-pink-100' : 'text-gray-600'
                }`}>
                  {feature.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Advantages Section
const AdvantagesSection = () => {
  return (
    <section className="py-12 md:py-16 bg-gradient-to-r from-pink-600 to-pink-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          <div className="text-white">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-6 md:mb-8">
              Advantages of becoming a customer of our company.
            </h2>
            
            <div className="space-y-4 md:space-y-6">
              <div className="flex items-start">
                <div className="bg-white text-pink-600 rounded-full p-2 mr-3 md:mr-4 mt-1 flex-shrink-0">
                  <span className="font-bold text-sm md:text-base lg:text-lg">1</span>
                </div>
                <div>
                  <h3 className="text-lg md:text-xl font-semibold mb-1 md:mb-2">Sell On The Go</h3>
                  <p className="text-pink-100 text-sm md:text-base">Complete a sale from anywhere in minutes using your phone.</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="bg-white text-pink-600 rounded-full p-2 mr-3 md:mr-4 mt-1 flex-shrink-0">
                  <span className="font-bold text-sm md:text-base lg:text-lg">2</span>
                </div>
                <div>
                  <h3 className="text-lg md:text-xl font-semibold mb-1 md:mb-2">Digital & Eco-Friendly</h3>
                  <p className="text-pink-100 text-sm md:text-base">Paperless processing that's faster and better for the planet.</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="bg-white text-pink-600 rounded-full p-2 mr-3 md:mr-4 mt-1 flex-shrink-0">
                  <span className="font-bold text-sm md:text-base lg:text-lg">3</span>
                </div>
                <div>
                  <h3 className="text-lg md:text-xl font-semibold mb-1 md:mb-2">Huge Brand Selection</h3>
                  <p className="text-pink-100 text-sm md:text-base">We accept hundreds of gift-card brands and any balance.</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="bg-white text-pink-600 rounded-full p-2 mr-3 md:mr-4 mt-1 flex-shrink-0">
                  <span className="font-bold text-sm md:text-base lg:text-lg">4</span>
                </div>
                <div>
                  <h3 className="text-lg md:text-xl font-semibold mb-1 md:mb-2">Fair, Upfront Offers</h3>
                  <p className="text-pink-100 text-sm md:text-base">Transparent quotes with clear fees — accept only if you're happy.</p>
                </div>
              </div>
            </div>
            
            <div className="mt-6 md:mt-8 flex flex-col sm:flex-row gap-3 md:gap-4">
              <a href="/getting-started" className="w-full sm:w-auto">
                <button className="w-full bg-white text-pink-600 hover:bg-gray-100 px-4 sm:px-6 md:px-8 py-3 rounded-lg font-semibold text-sm sm:text-base transition-colors">
                  Get Started
                </button>
              </a>
              <a href="/form-submission" className="w-full sm:w-auto">
                <button className="w-full border-2 border-white text-white hover:bg-white hover:text-pink-600 px-4 sm:px-6 md:px-8 py-3 rounded-lg font-semibold text-sm sm:text-base transition-colors">
                  Sell Gift Card
                </button>
              </a>
            </div>
          </div>
          
          <div className="relative mt-8 lg:mt-0 bg-transparent">
            <div className="clean-image-wrapper">
              <img 
                src="/images/trust-illustration.png" 
                alt="Happy satisfied customers celebrating success" 
                className="success-trophy-image w-full h-auto object-contain"
                style={{background: 'transparent'}}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// AI Fraud Detection Section - Enhanced Design with Advanced Animations
const AIFraudDetectionSection = () => {
  const [isVisible, setIsVisible] = React.useState(false);
  const [hoveredCard, setHoveredCard] = React.useState(null);
  const sectionRef = React.useRef(null);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <section ref={sectionRef} className="py-10 md:py-16 bg-gradient-to-b from-pink-50 via-purple-50 to-white relative overflow-hidden">
      {/* Decorative background elements with enhanced animations */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-20 left-10 w-64 h-64 bg-pink-300 rounded-full mix-blend-multiply filter blur-3xl animate-blob"></div>
        <div className="absolute top-40 right-10 w-64 h-64 bg-purple-300 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-2000"></div>
        <div className="absolute bottom-20 left-1/2 w-64 h-64 bg-pink-200 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-4000"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header with staggered animations */}
        <div className={`text-center mb-8 md:mb-12 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="inline-flex items-center justify-center w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br from-pink-500 via-pink-600 to-purple-600 rounded-full mb-4 md:mb-5 shadow-2xl">
            <Shield className={`w-8 h-8 md:w-10 md:h-10 text-white transition-all duration-700 ${isVisible ? 'scale-100 rotate-0' : 'scale-0 rotate-180'}`} />
          </div>
          <h2 className={`text-2xl md:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-pink-600 via-pink-700 to-purple-700 bg-clip-text text-transparent mb-3 md:mb-4 transition-all duration-1000 delay-100 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
            AI-Powered Fraud Detection Shield
          </h2>
          <p className={`text-base md:text-lg text-gray-700 max-w-3xl mx-auto font-medium transition-all duration-1000 delay-200 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
            Every gift card submission is automatically screened through our advanced AI security system, 
            protecting both buyers and sellers from fraudulent transactions.
          </p>
        </div>

        {/* Feature Cards Grid with enhanced animations */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-8 md:mb-10">
          {/* Card 1 - Image Analysis */}
          <div 
            className={`group bg-white rounded-xl p-5 md:p-6 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 border-2 border-pink-100 hover:border-pink-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} 
            style={{ transitionDelay: '300ms' }}
            onMouseEnter={() => setHoveredCard(1)}
            onMouseLeave={() => setHoveredCard(null)}
          >
            <div className={`w-12 h-12 md:w-14 md:h-14 bg-gradient-to-br from-pink-500 to-pink-600 rounded-xl flex items-center justify-center mb-4 transition-all duration-300 shadow-lg ${hoveredCard === 1 ? 'scale-110 rotate-6' : 'scale-100 rotate-0'}`}>
              <svg className="w-6 h-6 md:w-7 md:h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2 group-hover:text-pink-600 transition-colors">
              AI Vision Analysis
            </h3>
            <p className="text-sm md:text-base text-gray-600 leading-relaxed">
              Advanced computer vision scans every card image for tampering, photo manipulation, and authenticity markers.
            </p>
            <div className={`mt-3 h-1 bg-gradient-to-r from-pink-500 to-pink-600 rounded-full transition-all duration-500 ${hoveredCard === 1 ? 'w-full' : 'w-0'}`}></div>
          </div>

          {/* Card 2 - Duplicate Detection */}
          <div 
            className={`group bg-white rounded-xl p-5 md:p-6 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 border-2 border-purple-100 hover:border-purple-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} 
            style={{ transitionDelay: '450ms' }}
            onMouseEnter={() => setHoveredCard(2)}
            onMouseLeave={() => setHoveredCard(null)}
          >
            <div className={`w-12 h-12 md:w-14 md:h-14 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center mb-4 transition-all duration-300 shadow-lg ${hoveredCard === 2 ? 'scale-110 rotate-6' : 'scale-100 rotate-0'}`}>
              <svg className="w-6 h-6 md:w-7 md:h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2 group-hover:text-purple-600 transition-colors">
              Duplicate Detection
            </h3>
            <p className="text-sm md:text-base text-gray-600 leading-relaxed">
              Perceptual hashing technology identifies if the same card has been submitted multiple times across our system.
            </p>
            <div className={`mt-3 h-1 bg-gradient-to-r from-purple-500 to-purple-600 rounded-full transition-all duration-500 ${hoveredCard === 2 ? 'w-full' : 'w-0'}`}></div>
          </div>

          {/* Card 3 - Risk Scoring */}
          <div 
            className={`group bg-white rounded-xl p-5 md:p-6 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 border-2 border-pink-100 hover:border-pink-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} 
            style={{ transitionDelay: '600ms' }}
            onMouseEnter={() => setHoveredCard(3)}
            onMouseLeave={() => setHoveredCard(null)}
          >
            <div className={`w-12 h-12 md:w-14 md:h-14 bg-gradient-to-br from-pink-600 to-purple-600 rounded-xl flex items-center justify-center mb-4 transition-all duration-300 shadow-lg ${hoveredCard === 3 ? 'scale-110 rotate-6' : 'scale-100 rotate-0'}`}>
              <svg className="w-6 h-6 md:w-7 md:h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2 group-hover:text-pink-600 transition-colors">
              Real-Time Risk Scoring
            </h3>
            <p className="text-sm md:text-base text-gray-600 leading-relaxed">
              Each submission receives an instant fraud risk score based on multiple security factors and historical data patterns.
            </p>
          </div>
        </div>

        {/* Stats Bar - Enhanced */}
        <div className={`bg-gradient-to-r from-pink-600 via-pink-700 to-purple-700 rounded-2xl p-6 md:p-8 shadow-2xl mb-8 md:mb-10 transition-all duration-1000 ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`} style={{ transitionDelay: '400ms' }}>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 md:gap-8">
            <div className="text-center border-b sm:border-b-0 sm:border-r border-pink-400/30 pb-6 sm:pb-0 last:border-0">
              <div className="text-4xl md:text-5xl font-bold text-white mb-2 animate-count-up">
                99.7%
              </div>
              <div className="text-sm md:text-base text-pink-100 font-medium">
                Fraud Detection Accuracy
              </div>
            </div>
            <div className="text-center border-b sm:border-b-0 sm:border-r border-pink-400/30 pb-6 sm:pb-0 last:border-0">
              <div className="text-4xl md:text-5xl font-bold text-white mb-2 animate-count-up">
                {'<'}30s
              </div>
              <div className="text-sm md:text-base text-pink-100 font-medium">
                Average Screening Time
              </div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-white mb-2 animate-count-up">
                24/7
              </div>
              <div className="text-sm md:text-base text-pink-100 font-medium">
                Automated Protection
              </div>
            </div>
          </div>
        </div>

        {/* Benefits Grid */}
        <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5 mb-8 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} style={{ transitionDelay: '500ms' }}>
          <div className="flex items-start space-x-3 bg-white p-4 md:p-5 rounded-xl shadow-md hover:shadow-xl transition-shadow">
            <div className="flex-shrink-0">
              <CheckCircle className="w-6 h-6 text-pink-600" />
            </div>
            <div>
              <h4 className="font-bold text-gray-900 mb-1 text-base md:text-lg">Instant Verification</h4>
              <p className="text-sm text-gray-600">Automated checks complete in seconds, not hours</p>
            </div>
          </div>
          <div className="flex items-start space-x-3 bg-white p-4 md:p-5 rounded-xl shadow-md hover:shadow-xl transition-shadow">
            <div className="flex-shrink-0">
              <CheckCircle className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h4 className="font-bold text-gray-900 mb-1 text-base md:text-lg">Manual Review Backup</h4>
              <p className="text-sm text-gray-600">High-risk submissions flagged for human verification</p>
            </div>
          </div>
          <div className="flex items-start space-x-3 bg-white p-4 md:p-5 rounded-xl shadow-md hover:shadow-xl transition-shadow">
            <div className="flex-shrink-0">
              <CheckCircle className="w-6 h-6 text-pink-600" />
            </div>
            <div>
              <h4 className="font-bold text-gray-900 mb-1 text-base md:text-lg">Transparent Process</h4>
              <p className="text-sm text-gray-600">You'll know exactly why a card is flagged or approved</p>
            </div>
          </div>
          <div className="flex items-start space-x-3 bg-white p-4 md:p-5 rounded-xl shadow-md hover:shadow-xl transition-shadow">
            <div className="flex-shrink-0">
              <CheckCircle className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h4 className="font-bold text-gray-900 mb-1 text-base md:text-lg">Secure Evidence Storage</h4>
              <p className="text-sm text-gray-600">Encrypted storage with time-limited access links</p>
            </div>
          </div>
        </div>

        {/* CTA Button */}
        <div className={`text-center transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} style={{ transitionDelay: '600ms' }}>
          <a href="/form-submission">
            <button className="group bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-700 hover:to-purple-700 text-white px-8 md:px-10 py-4 rounded-xl font-bold text-base md:text-lg transition-all duration-300 inline-flex items-center space-x-3 shadow-xl hover:shadow-2xl hover:scale-105 transform">
              <Shield className="w-5 h-5 md:w-6 md:h-6 group-hover:rotate-12 transition-transform" />
              <span>Submit Your Card Securely</span>
              <svg className="w-4 h-4 md:w-5 md:h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </button>
          </a>
        </div>
      </div>
    </section>
  );
};

// Balance Verification Disclaimer Section
const BalanceDisclaimerSection = () => {
  const [isVisible, setIsVisible] = React.useState(false);
  const [isExpanded, setIsExpanded] = React.useState(false);
  const sectionRef = React.useRef(null);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <section ref={sectionRef} className="py-6 md:py-12 bg-gradient-to-r from-amber-50 via-orange-50 to-amber-50">
      <div className="max-w-4xl mx-auto px-3 sm:px-6 lg:px-8">
        <div 
          className={`bg-white rounded-xl sm:rounded-2xl shadow-lg border-2 border-amber-200 overflow-hidden transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-4 sm:px-6 py-3 sm:py-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-base sm:text-lg md:text-xl font-bold text-white leading-tight">
                Important Balance Verification Notice
              </h3>
            </div>
          </div>

          {/* Content */}
          <div className="p-4 sm:p-6">
            <p className="text-sm sm:text-base text-gray-700 mb-4 leading-relaxed">
              Many customers do <strong className="text-amber-700">not verify their gift card balance before submission</strong> and later claim that the balance was missing or reduced. We also regularly see customers <strong className="text-amber-700">submitting the same gift card on multiple platforms at the same time</strong> and then contacting us to ask why the balance is no longer available.
            </p>

            <div className="bg-amber-50 border border-amber-200 rounded-lg sm:rounded-xl p-3 sm:p-4 mb-4">
              <p className="font-semibold text-amber-800 mb-2 sm:mb-3 text-sm sm:text-base">To be absolutely clear:</p>
              <ul className="space-y-2 text-gray-700 text-sm sm:text-base">
                <li className="flex items-start gap-2">
                  <span className="text-amber-500 mt-0.5">•</span>
                  <span><strong>You MUST verify the gift card balance yourself</strong> before submitting.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-500 mt-0.5">•</span>
                  <span><strong>Submit your card to ONE platform only.</strong></span>
                </li>
              </ul>
            </div>

            {/* Expandable section */}
            <button 
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-amber-600 hover:text-amber-700 font-semibold flex items-center gap-2 mb-3 transition-colors text-sm sm:text-base"
            >
              {isExpanded ? 'Show Less' : 'Read Full Disclaimer'}
              <svg 
                className={`w-4 h-4 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`} 
                fill="none" stroke="currentColor" viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            <div className={`overflow-hidden transition-all duration-500 ${isExpanded ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'}`}>
              <div className="bg-gray-50 border border-gray-200 rounded-lg sm:rounded-xl p-3 sm:p-4 mb-3 sm:mb-4">
                <p className="text-sm sm:text-base text-gray-700 mb-2 sm:mb-3">
                  Once a card is submitted, <strong>we are NOT responsible</strong> for any balance loss, partial balance, or zero balance caused by:
                </p>
                <ul className="space-y-1 text-gray-600 ml-3 sm:ml-4 text-sm sm:text-base">
                  <li>• Prior use</li>
                  <li>• Parallel submissions on other platforms</li>
                  <li>• Delays in submission</li>
                  <li>• Card sharing or reuse</li>
                  <li>• Any activity outside our system</li>
                </ul>
              </div>

              <div className="bg-red-50 border border-red-200 rounded-lg sm:rounded-xl p-3 sm:p-4">
                <p className="text-red-800 font-semibold text-xs sm:text-sm leading-relaxed">
                  By submitting a gift card on our platform, <strong>you confirm that you have checked the balance and accept full responsibility</strong> for the card's condition at the time of submission.
                </p>
                <p className="text-red-700 font-bold mt-2 text-sm sm:text-base">
                  No exceptions. No disputes. No reversals.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// How It Works Process Section
const HowItWorksSection = () => {
  const steps = [
    {
      number: "1",
      icon: (
        <svg className="h-8 w-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      title: "Select your card from our Accepted Gift Cards tab.",
      description: "If it's not listed, contact support via email or Live Chat."
    },
    {
      number: "2", 
      icon: (
        <svg className="h-8 w-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      ),
      title: "Select your card in our Rate Calculator for an instant quote.",
      description: "Happy with the rate? Proceed to the next step."
    },
    {
      number: "3",
      icon: (
        <svg className="h-8 w-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
      ),
      title: "Fill in your gift card details and submit the form.",
      description: "Pick your payment method to complete."
    },
    {
      number: "4",
      icon: (
        <svg className="h-8 w-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      title: "We verify and approve your card.",
      description: "Funds are sent to your chosen payment method."
    }
  ];

  return (
    <section className="py-12 md:py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-10 md:mb-12">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4">
            <span className="text-pink-600">The all-in-one solution</span> for turning
            <br />
            your unused gift cards
            <br />
            <span className="text-gray-900">into instant cash.</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Steps */}
          <div className="space-y-6 md:space-y-8">
            {steps.map((step, index) => (
              <div key={index} className="scroll-move flex items-start">
                <div className="bg-pink-600 rounded-full w-12 h-12 md:w-14 md:h-14 flex items-center justify-center mr-4 md:mr-6 flex-shrink-0">
                  {step.icon}
                </div>
                <div>
                  <h3 className="text-lg md:text-xl font-semibold text-gray-900 mb-2">
                    {step.title}
                  </h3>
                  <p className="text-sm md:text-base text-gray-600">
                    {step.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Rate Calculator Mockup - Responsive */}
          <div className="relative flex justify-center lg:justify-end">
            <div className="relative">
              {/* Clean phone frame - much smaller on mobile */}
              <div className="w-48 sm:w-56 md:w-64 lg:w-80 xl:w-96 bg-black rounded-[2rem] sm:rounded-[2.5rem] md:rounded-[3rem] p-1 sm:p-1.5 md:p-2 shadow-2xl">
                <div className="bg-white rounded-[1.5rem] sm:rounded-[2rem] md:rounded-[2.5rem] p-4 sm:p-6 md:p-8 h-[400px] sm:h-[450px] md:h-[500px] lg:h-[600px] overflow-hidden">
                  {/* Screen content */}
                  <div className="flex flex-col h-full">
                    {/* Header */}
                    <div className="text-center mb-4 md:mb-6">
                      <h3 className="text-sm sm:text-base md:text-lg lg:text-xl font-bold text-blue-900 leading-tight">
                        Get the current value for your transaction
                      </h3>
                    </div>

                    {/* Gift Card Category */}
                    <div className="mb-3 md:mb-4">
                      <label className="block text-xs sm:text-sm font-semibold text-gray-900 mb-2 md:mb-3">
                        Gift Card Category<span className="text-red-500">*</span>
                      </label>
                      <div className="space-y-1.5 md:space-y-2">
                        <div className="flex items-center">
                          <div className="w-3 h-3 md:w-4 md:h-4 border-2 border-gray-300 rounded mr-2"></div>
                          <span className="text-xs md:text-sm text-gray-700">Card with Receipt</span>
                        </div>
                        <div className="flex items-center">
                          <div className="w-3 h-3 md:w-4 md:h-4 border-2 border-gray-300 rounded mr-2"></div>
                          <span className="text-xs md:text-sm text-gray-700">Card without Receipt</span>
                        </div>
                        <div className="flex items-center">
                          <div className="w-3 h-3 md:w-4 md:h-4 border-2 border-gray-300 rounded mr-2"></div>
                          <span className="text-xs md:text-sm text-gray-700">Card Partially Used</span>
                        </div>
                      </div>
                    </div>

                    {/* Input Fields */}
                    <div className="mb-2 md:mb-3">
                      <div className="border-2 border-gray-300 rounded-lg p-2 md:p-3 text-xs md:text-sm text-gray-400">
                        Gift Card Name
                      </div>
                    </div>
                    
                    <div className="mb-4 md:mb-6">
                      <div className="border-2 border-gray-300 rounded-lg p-2 md:p-3 text-xs md:text-sm text-gray-400">
                        Value of Gift Card
                      </div>
                    </div>

                    {/* Result Display */}
                    <div className="mb-4 md:mb-6 text-center">
                      <p className="text-xs md:text-sm font-semibold text-gray-900 mb-1">How much you will get</p>
                      <p className="text-xl sm:text-2xl md:text-3xl font-bold text-blue-900">0.00</p>
                    </div>

                    {/* Buttons */}
                    <div className="space-y-2 md:space-y-3">
                      <div className="bg-blue-600 text-white text-center py-2 md:py-3 px-3 md:px-4 rounded-full text-xs md:text-sm font-semibold">
                        Check Rate
                      </div>
                      <div className="border-2 border-gray-900 text-gray-900 text-center py-2 md:py-3 px-3 md:px-4 rounded-full text-xs md:text-sm font-semibold">
                        Proceed to Trade Card
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center mt-10 md:mt-12">
          <div className="bg-pink-100 p-3 md:p-4 rounded-lg inline-flex flex-col sm:flex-row items-center justify-center mb-6 mx-4 sm:mx-0">
            <svg className="h-5 w-5 md:h-6 md:w-6 text-pink-600 mb-2 sm:mb-0 sm:mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            <div className="text-center sm:text-left">
              <div className="text-pink-600 font-semibold text-sm md:text-base">CUSTOMERS SERVED TILL DATE</div>
              <AnimatedCounter target={874458} duration={2500} />
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto">
            <a href="/form-submission" className="flex-1">
              <button className="w-full bg-pink-600 text-white hover:bg-pink-700 px-6 py-3 rounded-lg font-semibold text-sm transition-colors">
                Start Trading Now
              </button>
            </a>
            <a href="/rate-calculator" className="flex-1">
              <button className="w-full border-2 border-pink-600 text-pink-600 hover:bg-pink-600 hover:text-white px-6 py-3 rounded-lg font-semibold text-sm transition-colors">
                Check Rates
              </button>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

// Testimonials Section
const TestimonialsSection = () => {
  const testimonials = [
    {
      name: "Jordan K., Chicago",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face",
      rating: 5,
      text: "I sold three cards in one go — each payout arrived the same day. CashifyGCmart made the whole process seamless."
    },
    {
      name: "Samantha W., Boston",
      image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face",
      rating: 5,
      text: "Reliable and transparent. CashifyGCmart is the best gift-card buyer I've tried."
    },
    {
      name: "Cameron P., Denver",
      image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face",
      rating: 5,
      text: "The whole process was effortless: clear instructions, quick verification, and a reliable payout from CashifyGCmart."
    }
  ];

  return (
    <section className="py-12 md:py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div className="mb-8 md:mb-10">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900">
            Recent <span className="text-pink-600">reviews</span> from our customers
          </h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 lg:gap-8">
          {testimonials.map((testimonial, index) => (
            <div key={index} className="scroll-move bg-gray-50 hover:bg-white p-4 md:p-6 lg:p-8 rounded-xl md:rounded-2xl transition-all duration-300 hover:scale-105 hover:shadow-xl cursor-pointer">
              <img 
                src={testimonial.image} 
                alt={testimonial.name}
                className="w-12 h-12 md:w-14 md:h-14 lg:w-16 lg:h-16 rounded-full mx-auto mb-3 md:mb-4 transition-transform duration-200 hover:scale-110"
              />
              <div className="flex justify-center mb-3 md:mb-4">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <span key={i} className="text-yellow-400 hover:text-yellow-500 text-lg md:text-xl transition-colors duration-150">&#9733;</span>
                ))}
              </div>
              <p className="text-gray-600 mb-4 md:mb-6 italic hover:text-gray-800 transition-colors duration-200 text-sm md:text-base">"{testimonial.text}"</p>
              <div className="font-semibold text-gray-900 hover:text-pink-600 transition-colors duration-200 text-sm md:text-base">{testimonial.name}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Footer Component
const Footer = () => {
  return (
    <footer className="bg-gray-900 text-white py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="md:col-span-1">
            <div className="flex items-center mb-4">
              <div className="bg-gradient-to-r from-pink-500 to-pink-600 p-2 rounded-full">
                <CreditCard className="h-6 w-6 text-white" />
              </div>
              <div className="ml-3 premium-brand">
                <div className="brand-name">Cashifygcmart</div>
                <div className="brand-tagline">Instant Offers, Same-Day Payments</div>
              </div>
            </div>
            <p className="text-gray-400 mb-6">
              Turn Gift Cards into Cash Instantly with Cashifygcmart! Trade unused gift cards 
              for quick cash - fast, easy and hassle-free!
            </p>
          </div>
          
          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
            <ul className="space-y-3 text-gray-400">
              <li><a href="/getting-started" className="hover:text-white transition-colors">Getting Started</a></li>
              <li><a href="/accepted-cards" className="hover:text-white transition-colors">Accepted Gift Cards</a></li>
              <li><a href="/form-submission" className="hover:text-white transition-colors">Form Submission</a></li>
              <li><a href="/rate-calculator" className="hover:text-white transition-colors">Rate Calculator</a></li>
            </ul>
          </div>
          
          {/* Menu */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Menu</h3>
            <ul className="space-y-3 text-gray-400">
              <li><a href="/faqs" className="hover:text-white transition-colors">FAQs</a></li>
              <li><a href="/terms-of-service" className="hover:text-white transition-colors">Terms of Service</a></li>
              <li><a href="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</a></li>
              <li><a href="/refund-policy" className="hover:text-white transition-colors">Refund Policy</a></li>
              <li><a href="/cookie-policy" className="hover:text-white transition-colors">Cookie Policy</a></li>
              <li><a href="/security" className="hover:text-white transition-colors">Security & Data Protection</a></li>
            </ul>
          </div>
          
          {/* Services */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Contact Us</h3>
            <ul className="space-y-3 text-gray-400">
              <li>📧 support@cashifygcmart.com</li>
              <li>📍 2099 Harborview Drive, Suite 210<br />San Diego, CA 92101</li>
            </ul>
          </div>
        </div>
        
        {/* Copyright */}
        <div className="mt-8 pt-8 border-t border-gray-800 text-center">
          <p className="text-gray-400">Copyright @cashifygcmart.com - All Rights Reserved.</p>
        </div>
      </div>
    </footer>
  );
};

// Main App Component
const Home = () => {
  useScrollMovement();
  
  return (
    <div>
      <Helmet>
        <title>Sell Gift Cards for Cash — Same-Day Payout | CashifyGCMart</title>
        <meta name="description" content="Sell unused gift cards for cash instantly. Up to 93% rates with same-day PayPal, Zelle, or CashApp payouts. 100% secure." />
        <link rel="canonical" href="https://cashifygcmart.com/" />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Sell Gift Cards for Cash — Same-Day Payout | CashifyGCMart" />
        <meta property="og:description" content="Sell unused gift cards for cash instantly. Up to 93% rates with same-day PayPal, Zelle, or CashApp payouts. 100% secure." />
        <meta property="og:url" content="https://cashifygcmart.com/" />
      </Helmet>
      <Header />
      <HeroSection />
      
      {/* Holiday Season Notice - Toggle via SHOW_HOLIDAY_NOTICE constant at top of file */}
      {SHOW_HOLIDAY_NOTICE && <HolidayNotice />}
      
      <AIFraudDetectionSection />
      <StatsSection />
      <AnimatedProcessSection />
      <WhyChooseUsSection />
      <AdvantagesSection />
      <HowItWorksSection />
      <TestimonialsSection />
      <Footer />
    </div>
  );
};

// Wrapper component to conditionally render chat widget based on route
// Christmas Theme Wrapper - Conditionally shows Christmas elements based on route
const ChristmasThemeWrapper = () => {
  const location = useLocation();
  
  // Hide Santa on form submission page to avoid distractions while filling form
  const hideOnPages = ['/form-submission'];
  const shouldHideSanta = hideOnPages.includes(location.pathname);
  
  return (
    <>
      {isChristmasSeason() && (
        <>
          <Snowflakes intensity="medium" />
          {!shouldHideSanta && <ChristmasSanta position="bottom-right" />}
        </>
      )}
    </>
  );
};

const ChatWidgetWrapper = ({ liveChatOpen, setLiveChatOpen }) => {
  const location = useLocation();
  
  // Hide chat widget on admin pages
  const isAdminPage = location.pathname.startsWith('/admin');
  
  if (isAdminPage) {
    return null; // Don't render chat on admin pages
  }
  
  return (
    <>
      {/* Unified Support Button - Single bubble with all options */}
      <UnifiedSupportButton setLiveChatOpen={setLiveChatOpen} />
      
      {/* Smart Live Chat Widget - AI support with automatic transfer to Zoho human agents */}
      <LiveChatWidget isTriggered={liveChatOpen} onClose={() => setLiveChatOpen(false)} />
    </>
  );
};

function App() {
  const [liveChatOpen, setLiveChatOpen] = React.useState(false);

  return (
    <HelmetProvider>
      <BrowserRouter>
        <div className="App">
          <ChristmasThemeWrapper />
          
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/sell-gift-cards-online" element={<><Header /><SellGiftCardsOnline /><Footer /></>} />
            <Route path="/rate-calculator" element={<><Header /><RateCalculator /><Footer /></>} />
            <Route path="/form-submission" element={<><Header /><FormSubmission /><Footer /></>} />
            <Route path="/track-submission" element={<><Header /><TrackSubmission /><Footer /></>} />
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/adwords-dashboard" element={<AdWordsDashboard />} />
            <Route path="/accepted-cards" element={<><Header /><AcceptedGiftCards /><Footer /></>} />
            <Route path="/faqs" element={<><Header /><FAQs /><Footer /></>} />
            <Route path="/getting-started" element={<><Header /><GettingStarted /><Footer /></>} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/refund-policy" element={<RefundPolicy />} />
            <Route path="/cookie-policy" element={<CookiePolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/security" element={<SecurityPage />} />
            <Route path="/guide/how-to-sell-gift-cards" element={<><Header /><GiftCardGuide /><Footer /></>} />
            <Route path="/gift-card-values" element={<><Header /><GiftCardValues /><Footer /></>} />
            <Route path="/safety-guide" element={<><Header /><SafetyGuide /><Footer /></>} />
            <Route path="/seasonal-trends" element={<><Header /><SeasonalTrends /><Footer /></>} />
            <Route path="/compare-rates" element={<><Header /><CompareRates /><Footer /></>} />
            <Route path="/sell-amazon-gift-cards" element={<><Header /><BrandLandingPage /><Footer /></>} />
            <Route path="/sell-apple-gift-cards" element={<><Header /><BrandLandingPage /><Footer /></>} />
            <Route path="/sell-walmart-gift-cards" element={<><Header /><BrandLandingPage /><Footer /></>} />
            <Route path="/sell-target-gift-cards" element={<><Header /><BrandLandingPage /><Footer /></>} />
            <Route path="/sell-bestbuy-gift-cards" element={<><Header /><BrandLandingPage /><Footer /></>} />
            <Route path="/sell-homedepot-gift-cards" element={<><Header /><BrandLandingPage /><Footer /></>} />
            <Route path="/sell-lowes-gift-cards" element={<><Header /><BrandLandingPage /><Footer /></>} />
            <Route path="/sell-macys-gift-cards" element={<><Header /><BrandLandingPage /><Footer /></>} />
            <Route path="/sell-lululemon-gift-cards" element={<><Header /><BrandLandingPage /><Footer /></>} />
            <Route path="/sell-dicks-gift-cards" element={<><Header /><BrandLandingPage /><Footer /></>} />
            <Route path="/sell-sephora-gift-cards" element={<><Header /><BrandLandingPage /><Footer /></>} />
            <Route path="/sell-starbucks-gift-cards" element={<><Header /><BrandLandingPage /><Footer /></>} />
            <Route path="/sell-disney-gift-cards" element={<><Header /><BrandLandingPage /><Footer /></>} />
            <Route path="/sell-nike-gift-cards" element={<><Header /><BrandLandingPage /><Footer /></>} />
            <Route path="/sell-ebay-gift-cards" element={<><Header /><BrandLandingPage /><Footer /></>} />
            <Route path="/sell-steam-gift-cards" element={<><Header /><BrandLandingPage /><Footer /></>} />
            <Route path="/sell-razergold-gift-cards" element={<><Header /><BrandLandingPage /><Footer /></>} />
            <Route path="/sell-ulta-gift-cards" element={<><Header /><BrandLandingPage /><Footer /></>} />
            <Route path="/sell-amex-gift-cards" element={<><Header /><BrandLandingPage /><Footer /></>} />
            <Route path="/sell-visa-gift-cards" element={<><Header /><BrandLandingPage /><Footer /></>} />
            <Route path="/brand/:brand" element={<><Header /><BrandPage /><Footer /></>} />
            <Route path="/location/:city/:state" element={<><Header /><LocationPage /><Footer /></>} />
            <Route path="/blog" element={<><Header /><BlogIndex /><Footer /></>} />
            <Route path="/blog/:slug" element={<><Header /><BlogPost /><Footer /></>} />
            <Route path="/evidence/:referenceNumber" element={<EvidenceViewer />} />
            <Route path="*" element={<><Header /><NotFound /><Footer /></>} />
          </Routes>
          
          {/* Conditional Chat Widget - Hidden on admin pages */}
          <ChatWidgetWrapper liveChatOpen={liveChatOpen} setLiveChatOpen={setLiveChatOpen} />
        </div>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;